/**
 * The browser-owned durable store behind the vault worker.
 *
 * One IndexedDB database per account partition holds notes, their history,
 * and sync receipts. Every tab of the same account opens the same database,
 * which is what lets two tabs see each other's saves — and what makes a save
 * on a stale base revision possible. The store is deliberately dumb: the
 * conflict rule lives in `sync-state.ts`, where it can be tested without a
 * database.
 */

import type { NoteVersion, VaultNote } from "./types.js";

export type StoreName = "notes" | "history" | "receipts" | "requests" | "meta";

/** What `applyNote` writes: the note's next state and, optionally, one history entry. */
export interface NoteWrite {
  readonly note: VaultNote;
  readonly history?: NoteVersion;
}

/**
 * Decides a note write against the copy the store holds at this instant.
 *
 * It runs inside the IndexedDB transaction, which commits as soon as control
 * returns to the event loop, so it must be synchronous: the return type rules
 * out a promise. `undefined` writes nothing.
 */
export type NoteDecision = (current: VaultNote | undefined, history: readonly NoteVersion[]) => NoteWrite | undefined;

/** A decision that may also remove the note: `"remove"` deletes it and keeps its history. */
export type NoteChangeDecision = (current: VaultNote | undefined, history: readonly NoteVersion[]) => NoteWrite | "remove" | undefined;

/** One note's part of a batch. */
export interface NoteChange {
  readonly id: string;
  readonly decide: NoteChangeDecision;
}

/** What one change in a batch did: the write, or the note it removed. */
export type NoteChangeResult =
  | { readonly id: string; readonly written: NoteWrite }
  | { readonly id: string; readonly removed: VaultNote };

export interface NoteStore {
  all<T>(store: StoreName): Promise<T[]>;
  put<T extends { readonly id: string }>(store: StoreName, value: T): Promise<void>;
  remove(store: StoreName, id: string): Promise<void>;
  replaceRequest<T extends { readonly id: "pending"; readonly revision?: string }>(expectedRevision: string | null, value?: T): Promise<void>;
  /**
   * Reads note `id` and its history, lets `decide` choose the write, and
   * commits note and history in one transaction — both or neither. Every note
   * write goes through here, so no writer decides against a copy that another
   * tab or an in-flight sync has since replaced. Resolves to what was written,
   * or `undefined` when `decide` declined.
   */
  applyNote(id: string, decide: NoteDecision): Promise<NoteWrite | undefined>;
  /**
   * `applyNote` for many notes in one transaction: every decision sees the
   * copy stored at that instant, and the whole batch commits or none of it.
   * A pull lands a page this way instead of one transaction per note.
   * Resolves to what each change did, omitting the ones that declined.
   */
  applyNotes(changes: readonly NoteChange[]): Promise<NoteChangeResult[]>;
  close(): void;
}

export class RequestDraftConflict extends Error {
  constructor() { super("This saved request changed in another tab. Reopen it before editing or retrying. Your text remains on this page."); }
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}

/** Opens (creating on first use) the partition's database. */
export async function openNoteStore(databaseName: string, factory: IDBFactory): Promise<NoteStore> {
  const request = factory.open(databaseName, 3);
  request.onupgradeneeded = () => {
    const next = request.result;
    if (!next.objectStoreNames.contains("notes")) next.createObjectStore("notes", { keyPath: "id" });
    if (!next.objectStoreNames.contains("history")) {
      const history = next.createObjectStore("history", { keyPath: "id" });
      history.createIndex("noteId", "noteId", { unique: false });
    }
    if (!next.objectStoreNames.contains("receipts")) next.createObjectStore("receipts", { keyPath: "id" });
    if (!next.objectStoreNames.contains("requests")) next.createObjectStore("requests", { keyPath: "id" });
    if (!next.objectStoreNames.contains("meta")) next.createObjectStore("meta", { keyPath: "id" });
  };
  return new IndexedDbNoteStore(await requestValue(request));
}

class IndexedDbNoteStore implements NoteStore {
  constructor(private readonly database: IDBDatabase) { database.onversionchange = () => database.close(); }

  all<T>(store: StoreName): Promise<T[]> {
    return requestValue(this.database.transaction(store, "readonly").objectStore(store).getAll()) as Promise<T[]>;
  }

  async put<T extends { readonly id: string }>(store: StoreName, value: T): Promise<void> {
    const transaction = this.database.transaction(store, "readwrite");
    transaction.objectStore(store).put(value);
    await transactionDone(transaction);
  }

  async remove(store: StoreName, id: string): Promise<void> {
    const transaction = this.database.transaction(store, "readwrite");
    transaction.objectStore(store).delete(id);
    await transactionDone(transaction);
  }

  replaceRequest<T extends { readonly id: "pending"; readonly revision?: string }>(expectedRevision: string | null, value?: T): Promise<void> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction("requests", "readwrite");
      const records = transaction.objectStore("requests");
      const read = records.get("pending");
      let conflict = false;
      read.onsuccess = () => {
        const current = read.result as { revision?: string } | undefined;
        const revision = current === undefined ? null : current.revision ?? "legacy";
        if (revision !== expectedRevision) { conflict = true; transaction.abort(); return; }
        if (value === undefined) records.delete("pending");
        else records.put(value);
      };
      transaction.oncomplete = () => resolve();
      transaction.onerror = transaction.onabort = () => reject(conflict ? new RequestDraftConflict() : transaction.error ?? new Error("Request persistence failed"));
    });
  }

  async applyNote(id: string, decide: NoteDecision): Promise<NoteWrite | undefined> {
    const [result] = await this.applyNotes([{ id, decide }]);
    return result && "written" in result ? result.written : undefined;
  }

  applyNotes(changes: readonly NoteChange[]): Promise<NoteChangeResult[]> {
    return new Promise((resolve, reject) => {
      const transaction = this.database.transaction(["notes", "history"], "readwrite");
      const notes = transaction.objectStore("notes");
      const history = transaction.objectStore("history");
      // A store created before the index existed is read whole and filtered.
      const indexed = history.indexNames.contains("noteId");
      const results: NoteChangeResult[] = [];
      let failure: unknown;
      const decideOne = (change: NoteChange): void => {
        const current = notes.get(change.id);
        const versions = indexed ? history.index("noteId").getAll(change.id) : history.getAll();
        versions.onsuccess = () => {
          try {
            const found = versions.result as NoteVersion[];
            const stored = current.result as VaultNote | undefined;
            const decision = change.decide(stored, indexed ? found : found.filter(({ noteId }) => noteId === change.id));
            if (decision === "remove") {
              if (!stored) return;
              notes.delete(change.id);
              results.push({ id: change.id, removed: stored });
              return;
            }
            if (!decision) return;
            notes.put(decision.note);
            if (decision.history) history.put(decision.history);
            results.push({ id: change.id, written: decision });
          } catch (error) {
            failure = error;
            transaction.abort();
          }
        };
      };
      for (const change of changes) decideOne(change);
      transaction.oncomplete = () => resolve(results);
      transaction.onerror = transaction.onabort = () => reject(failure ?? transaction.error ?? new Error("Note write failed"));
    });
  }

  close(): void {
    this.database.close();
  }
}

/** The fallback when the browser refuses IndexedDB: the session lives in memory. */
export class MemoryNoteStore implements NoteStore {
  private readonly stores: Record<StoreName, Map<string, unknown>> = {
    notes: new Map(),
    history: new Map(),
    receipts: new Map(),
    requests: new Map(),
    meta: new Map(),
  };

  all<T>(store: StoreName): Promise<T[]> {
    return Promise.resolve([...this.stores[store].values()] as T[]);
  }

  put<T extends { readonly id: string }>(store: StoreName, value: T): Promise<void> {
    this.stores[store].set(value.id, value);
    return Promise.resolve();
  }

  remove(store: StoreName, id: string): Promise<void> {
    this.stores[store].delete(id);
    return Promise.resolve();
  }

  replaceRequest<T extends { readonly id: "pending"; readonly revision?: string }>(expectedRevision: string | null, value?: T): Promise<void> {
    const current = this.stores.requests.get("pending") as { revision?: string } | undefined;
    if ((current === undefined ? null : current.revision ?? "legacy") !== expectedRevision) return Promise.reject(new RequestDraftConflict());
    if (value === undefined) this.stores.requests.delete("pending");
    else this.stores.requests.set("pending", value);
    return Promise.resolve();
  }

  async applyNote(id: string, decide: NoteDecision): Promise<NoteWrite | undefined> {
    const [result] = await this.applyNotes([{ id, decide }]);
    return result && "written" in result ? result.written : undefined;
  }

  applyNotes(changes: readonly NoteChange[]): Promise<NoteChangeResult[]> {
    // Decide against working copies and publish them only if every change is
    // valid, as the transaction would.
    const notes = new Map(this.stores.notes);
    const history = new Map(this.stores.history);
    const results: NoteChangeResult[] = [];
    try {
      for (const change of changes) {
        const current = notes.get(change.id) as VaultNote | undefined;
        const versions = ([...history.values()] as NoteVersion[]).filter(({ noteId }) => noteId === change.id);
        const decision = change.decide(current, versions);
        if (decision === "remove") {
          if (!current) continue;
          notes.delete(change.id);
          results.push({ id: change.id, removed: current });
          continue;
        }
        if (!decision) continue;
        if (typeof decision.note.id !== "string" || (decision.history && typeof decision.history.id !== "string")) {
          throw new Error("A note write needs keys for the note and its history entry.");
        }
        notes.set(decision.note.id, decision.note);
        if (decision.history) history.set(decision.history.id, decision.history);
        results.push({ id: change.id, written: decision });
      }
    } catch (error) {
      return Promise.reject(error instanceof Error ? error : new Error("Note write failed"));
    }
    this.stores.notes = notes;
    this.stores.history = history;
    return Promise.resolve(results);
  }

  close(): void {
    for (const store of Object.values(this.stores)) store.clear();
  }
}
